import type { Action, Thunk, Computed } from 'easy-peasy';
import { action, thunk, computed } from 'easy-peasy';
import type { StoreModel } from '..';
import type { Injections } from '../services/injections';
import type {
  Partner,
  DeliveryExperienceConfigurationInfo,
  PartnerCostingConfigurationInfo,
  MegaMethodTimeoutConfigurationInfo,
  UpdateVolumeEstimatesParams,
  UpdateReportingConfigParams,
  UpdateGeneralConfigParams,
  PasswordConfigurationInfo,
  V1AssetHistory,
  UpdateAddressConfigParams,
} from '../services/PartnerService/types';
import type { ApplicationErrorOptions, SetupStep } from '../../types';
import { PartnerSetupSteps } from '../../types';
import type { ReturnServiceMethod } from '../services/PartnerService/types';
import parseStatusCode from '../errors/parseStatusCode';
import { getSetupSteps } from './getSetupSteps';
import type { LabelAugmentation } from '../services/CarrierService/types';

export interface CountryCode {
  name: string;
  isoCountryCode: string;
}

export interface PartnerModel {
  // data
  activePartner?: Partner;
  checkFeatureToggle: Computed<PartnerModel, (featureFlag: string) => boolean>;
  checkFeatureConfigurationInfo: Computed<PartnerModel, (featureConfigurationProperty: string) => string | undefined>;
  lastAction?: number;
  setupSteps: Computed<PartnerModel, SetupStep[]>;
  setupCompletionPercentage: Computed<PartnerModel, number>;
  volumeEstimateSetup: Computed<PartnerModel, boolean>;
  reportingConfigSetup: Computed<PartnerModel, boolean>;

  setActivePartner: Action<PartnerModel, Partner>;
  partnerActionStart: Action<PartnerModel, void>;

  // Partner Config
  updateVolumeEstimates: Thunk<
    PartnerModel,
    UpdateVolumeEstimatesParams,
    Injections,
    StoreModel,
    Promise<Partner | undefined>
  >;
  updateReportingConfig: Thunk<
    PartnerModel,
    UpdateReportingConfigParams,
    Injections,
    StoreModel,
    Promise<Partner | undefined>
  >;
  updateDeliveryConfig: Thunk<
    PartnerModel,
    DeliveryExperienceConfigurationInfo,
    Injections,
    StoreModel,
    Promise<Partner | undefined>
  >;
  updateGeneralConfig: Thunk<
    PartnerModel,
    UpdateGeneralConfigParams,
    Injections,
    StoreModel,
    Promise<Partner | undefined>
  >;
  updateAddressConfig: Thunk<
    PartnerModel,
    UpdateAddressConfigParams,
    Injections,
    StoreModel,
    Promise<Partner | undefined>
  >;
  updateCostingConfig: Thunk<
    PartnerModel,
    PartnerCostingConfigurationInfo,
    Injections,
    StoreModel,
    Promise<Partner | undefined>
  >;
  updateTimeoutConfig: Thunk<
    PartnerModel,
    MegaMethodTimeoutConfigurationInfo,
    Injections,
    StoreModel,
    Promise<Partner | undefined>
  >;
  fetchPartner: Thunk<PartnerModel, string, Injections, StoreModel, Promise<Partner | undefined>>;
  partnerConfigFailure: Action<PartnerModel, ApplicationErrorOptions>;
  verifyEmailConfirmationToken: Thunk<PartnerModel, string, Injections, Promise<void>>;
  partnerEmailVerificationFailure: Action<PartnerModel, ApplicationErrorOptions>;
  setEmailVerified: Action<PartnerModel, boolean>;
  emailVerified: boolean;

  // Return Service Method
  upsertReturnsServiceMethod: Thunk<
    PartnerModel,
    Partial<ReturnServiceMethod>,
    Injections,
    StoreModel,
    Promise<Partner>
  >;
  // MVP implementation is support for a single returnServiceMethod
  returnServiceMethod: Computed<PartnerModel, ReturnServiceMethod | undefined>;
  returnsAugmentationList: Computed<PartnerModel, LabelAugmentation[]>;

  updatePartnerPasswordSettings: Thunk<
    PartnerModel,
    PasswordConfigurationInfo,
    Injections,
    StoreModel,
    Promise<Partner>
  >;

  updatePartner: Thunk<PartnerModel, Partner, Injections, StoreModel, Promise<Partner>>;

  // asset changes history audit
  setV1AssetHistory: Action<PartnerModel, V1AssetHistory[]>;
  fetchV1AssetHistory: Thunk<
    PartnerModel,
    { endDate: string; startDate: string; assetTypes: string },
    Injections,
    StoreModel,
    Promise<V1AssetHistory[]>
  >;
  v1AssetHistory: V1AssetHistory[];
}

export const partnerModel: PartnerModel = {
  v1AssetHistory: [],
  returnServiceMethod: computed((state) => {
    return (state.activePartner?.returnsServiceMethods || [])[0];
  }),
  returnsAugmentationList: computed((state) => {
    return (
      state.returnServiceMethod?.labelAugmentations?.map((val) => ({
        ...val,
        serviceMethodId: state.returnServiceMethod?.carrierServiceMethodId,
        serviceMethodName: state.returnServiceMethod?.name,
      })) || []
    );
  }),
  emailVerified: false,
  setActivePartner: action((state, payload) => {
    state.activePartner = payload;
  }),
  updatePartner: thunk(async (actions, payload, { injections }) => {
    try {
      const { partnerService } = injections;
      actions.partnerActionStart();
      const response = await partnerService.updatePartner(payload);
      actions.setActivePartner(response);
      return response;
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to update partner.',
      });
      throw error;
    }
  }),
  // can use global store state in computed property this way :)
  // add additional elements to array and arguments to callback function as
  // we figure out completion logic
  setupSteps: computed(
    [
      (state: any, storeState: any) => storeState.users.usersInvited,
      (state: any, storeState: any) => storeState.fulfillmentCenterConfig.networkSetupComplete,
      (state: any, storeState: any) => storeState.partner.volumeEstimateSetup,
      (state: any, storeState: any) => storeState.partner.reportingConfigSetup,
      (state: any, storeState: any) => storeState.carrier.configuredCarrierAccounts?.length,
      (state: any, storeState: any) => storeState.partner.partnerPackaging?.length,
      (state: any, storeState: any) => storeState.carrier.configuredCarrierAccounts?.length,
      (state: any, storeState: any) => storeState.security.partnerOptIns,
    ] as any,
    ((
      usersInvited: any,
      fcSetup: any,
      volumeSetup: any,
      reportingSetup: any,
      carrierAccountStep: any,
      packageConfigurationStep: any,
      labelConfigurationStep: any,
      partnerOptins: any,
    ) => {
      let steps: any[] = [];
      if (partnerOptins) {
        const setupSteps = getSetupSteps(partnerOptins);
        steps = [
          setupSteps.REGISTER
            ? {
                description: PartnerSetupSteps.REGISTER.description,
                title: PartnerSetupSteps.REGISTER.title,
                complete: true,
                path: '',
              }
            : null,
          setupSteps.INVITE
            ? {
                description: PartnerSetupSteps.INVITE.description,
                title: PartnerSetupSteps.INVITE.title,
                complete: usersInvited,
                path: 'team-management/manage-users',
              }
            : null,
          setupSteps.NETWORK_CONFIG
            ? {
                description: PartnerSetupSteps.NETWORK_CONFIG.description,
                title: PartnerSetupSteps.NETWORK_CONFIG.title,
                complete: fcSetup,
                path: 'configure/fulfillment-centers',
              }
            : null,
          setupSteps.VOLUME_ESTIMATES
            ? {
                description: PartnerSetupSteps.VOLUME_ESTIMATES.description,
                title: PartnerSetupSteps.VOLUME_ESTIMATES.title,
                complete: volumeSetup,
                path: 'configure/partner',
              }
            : null,
          setupSteps.REPORTING_CONFIG
            ? {
                description: PartnerSetupSteps.REPORTING_CONFIG.description,
                title: PartnerSetupSteps.REPORTING_CONFIG.title,
                complete: reportingSetup,
                path: 'configure/partner',
              }
            : null,
          setupSteps.CARRIER_ACCOUNT
            ? {
                description: PartnerSetupSteps.CARRIER_ACCOUNT.description,
                title: PartnerSetupSteps.CARRIER_ACCOUNT.title,
                complete: carrierAccountStep,
                path: 'configure/carriers',
              }
            : null,
          setupSteps.PACKAGE_CONFIGURATION
            ? {
                description: PartnerSetupSteps.PACKAGE_CONFIGURATION.description,
                title: PartnerSetupSteps.PACKAGE_CONFIGURATION.title,
                complete: packageConfigurationStep,
                path: 'configure/packaging',
              }
            : null,
          setupSteps.LABEL_CONFIGURATION
            ? {
                description: PartnerSetupSteps.LABEL_CONFIGURATION.description,
                title: PartnerSetupSteps.LABEL_CONFIGURATION.title,
                complete: labelConfigurationStep,
                path: 'configure/label-configuration',
              }
            : null,
        ].filter((val) => !!val);
      }
      return steps;
    }) as any,
  ),
  setupCompletionPercentage: computed(
    [(state: any, storeState: any) => storeState.partner.setupSteps],
    (setupSteps) => {
      const completed = setupSteps.reduce((accumulator: number, step: SetupStep) => {
        if (step.complete) {
          return (accumulator += 1);
        }
        return accumulator;
      }, 0);
      return Math.floor((100 * completed) / setupSteps.length);
    },
  ),
  partnerActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  partnerConfigFailure: action((state, error) => {
    console.error(error);
  }),
  updateVolumeEstimates: thunk(async (actions, payload, { injections }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const response = await partnerService.updateVolumeEstimates(payload);
      actions.setActivePartner(response);
      return response;
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating partner configuration.',
        asNotification: true,
      });
      throw error;
    }
  }),
  volumeEstimateSetup: computed((state) => {
    if (state.activePartner) {
      return state.activePartner?.pageViewEstimates
        ? Object.keys(state.activePartner?.pageViewEstimates).length > 0
        : false;
    }
    return false;
  }),
  reportingConfigSetup: computed((state) => {
    if (state.activePartner) {
      return state.activePartner?.reportingConfigurationInfo
        ? Object.keys(state.activePartner?.reportingConfigurationInfo).length > 0
        : false;
    }
    return false;
  }),
  updateCostingConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const state = getStoreState();
      if (!state.partner.activePartner) {
        throw Error;
      }
      const response = await partnerService.updatePartnerConfig({
        ...state.partner.activePartner,
        costingConfigurationInfo: payload,
      });
      if (state.partner.activePartner) {
        const updatedPartner: Partner = response;
        actions.setActivePartner(updatedPartner);
        return updatedPartner;
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating configuration.',
      });
      throw error;
    }
  }),
  updateGeneralConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const state = getStoreState();
      if (!state.partner.activePartner) {
        throw Error;
      }
      const response = await partnerService.updatePartnerConfig({ ...state.partner.activePartner, ...payload });
      if (state.partner.activePartner) {
        const updatedPartner: Partner = response;
        actions.setActivePartner(updatedPartner);
        return updatedPartner;
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating configuration.',
      });
      throw error;
    }
  }),
  updateAddressConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const state = getStoreState();
      if (!state.partner.activePartner) {
        throw Error;
      }
      const response = await partnerService.updatePartnerConfig({
        ...state.partner.activePartner,
        addressConfigurationInfo: payload,
      });
      if (state.partner.activePartner) {
        const updatedPartner: Partner = response;
        actions.setActivePartner(updatedPartner);
        return updatedPartner;
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating configuration.',
      });
      throw error;
    }
  }),
  updateDeliveryConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const state = getStoreState();
      if (!state.partner.activePartner) {
        throw Error;
      }
      const response = await partnerService.updatePartnerConfig({
        ...state.partner.activePartner,
        deliveryExperienceConfigurationInfo: {
          ...state.partner.activePartner.deliveryExperienceConfigurationInfo,
          ...payload,
        },
      });

      if (state.partner.activePartner) {
        const updatedPartner: Partner = response;
        actions.setActivePartner(updatedPartner);
        return updatedPartner;
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating configuration.',
      });
      throw error;
    }
  }),
  updateTimeoutConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const state = getStoreState();
      if (!state.partner.activePartner) {
        throw Error('No active partner found');
      }
      const response: Partner = await partnerService.updatePartnerConfig({
        ...state.partner.activePartner,
        megaMethodTimeoutConfigurationInfo: { ...payload },
      });
      if (state.partner.activePartner) {
        actions.setActivePartner(response);
        return response;
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating configuration.',
      });
      throw error;
    }
  }),
  updateReportingConfig: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const state = getStoreState();
      const response = await partnerService.updateReportingConfig(payload);
      // gateway doesn't return entire partner
      if (state.partner.activePartner) {
        const updatedPartner: Partner = { ...state.partner.activePartner, reportingConfigurationInfo: response };
        actions.setActivePartner(updatedPartner);
        return updatedPartner;
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating configuration.',
      });
      throw error;
    }
  }),
  fetchPartner: thunk(async (actions, payload, { injections }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const response = await partnerService.fetchPartner(payload);
      if (!response) {
        throw new Error('Error fetching partner');
      } else {
        actions.setActivePartner(response);
        return response;
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching partner configuration.',
        asNotification: false,
      });
    }
  }),
  partnerEmailVerificationFailure: action((actions, error) => {
    console.log(error);
  }),
  setEmailVerified: action((state, payload) => {
    state.emailVerified = payload;
  }),
  verifyEmailConfirmationToken: thunk(async (actions, payload, { injections }) => {
    try {
      const { partnerService } = injections;
      actions.partnerActionStart();

      await partnerService.verifyEmailConfirmationToken(payload);
      actions.setEmailVerified(true);
    } catch (error) {
      actions.setEmailVerified(false);
      actions.partnerEmailVerificationFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Verification of email failed',
        duration: 0,
      });
    }
  }),

  upsertReturnsServiceMethod: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.partnerActionStart();
      const { partnerService } = injections;
      const state = getStoreState();
      const partnerId = state.session.user?.sessionPartnerId;

      if (!partnerId) {
        throw new Error('Session error, no sessionPartnerId');
      }
      const response = await partnerService.upsertReturnServiceMethod(partnerId, payload);
      actions.setActivePartner(response);
      if (!response) {
        throw new Error('Error updating Partner Returns Service Method');
      } else {
        return response;
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Error updating Partner Returns Service Method',
        asNotification: true,
        duration: 0,
      });
      throw error;
    }
  }),
  updatePartnerPasswordSettings: thunk(async (actions, payload, { injections, getStoreState }) => {
    actions.partnerActionStart();
    try {
      const partnerId = getStoreState().session.user?.sessionPartnerId;
      if (partnerId) {
        return await injections.partnerService.updatePartnerPasswordSettings(partnerId, payload);
      } else {
        throw new Error('No loggedin partner');
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  checkFeatureToggle: computed((state) => (featureFlag: string) => {
    const toggles = state.activePartner?.featureToggles || {};
    return toggles[featureFlag] === 'true';
  }),
  checkFeatureConfigurationInfo: computed((state) => (featureConfigurationProperty: string) => {
    const featureConfigruationInfo = state.activePartner?.featureConfigurationInfo || {};
    return featureConfigruationInfo[featureConfigurationProperty];
  }),
  setV1AssetHistory: action((state, assetPayload) => {
    state.v1AssetHistory = assetPayload;
  }),
  fetchV1AssetHistory: thunk(async (actions, payload, { injections, getStoreState }) => {
    actions.partnerActionStart();
    try {
      const partnerId = getStoreState().session.user?.sessionPartnerId;
      if (partnerId) {
        const res = await injections.partnerService.fetchV1AssetHistory(partnerId, payload);
        actions.setV1AssetHistory(res);
        return res;
      } else {
        throw new Error('No loggedin partner');
      }
    } catch (error) {
      actions.partnerConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
};
